import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../../context/Auth";
import { useParams } from "react-router-dom";
import ExportData from "../../../components/common/ExportData";
import { useGlobalContext } from "../../../context/stateContext";
import BranchBulkReconciliation from "../BranchBulkReconciliation";
import BranchBulkDrugUploadModal from "../BranchBulkDrugUploadModal";
import {
  ChevronLeft,
  ChevronRight,
  Package,
  Upload,
  RefreshCw,
} from "lucide-react";
import DebouncedSearchInput from "../../../components/common/DebouncedSearchInput";
import RoleBasedComponent from "../../../components/RoleBasedComponent";
import SortableTableHeader from "../../../components/Table/SortableTableHeader";
import { BiSync } from "react-icons/bi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const BranchInventory = () => {
  const { activeSession } = useGlobalContext();
  const { branch_id } = useParams();
  const { requestWithAuth } = useAuth();
  const [inventoryList, setInventoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bulkDrugModalOpen, setBulkDrugModalOpen] = useState(false);
  const [branch, setBranch] = useState(null);
  const [allInventory, setAllInventory] = useState([]);
  const [usingBackendPagination, setUsingBackendPagination] = useState(true);
  const [fileName, setFileName] = useState("Branch Inventory");
  const [syncing, setSyncing] = useState(false);
  const fullDataFetched = useRef(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchBranch = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/${branch_id}`,
        "GET"
      );
      setBranch(data);
      const fileName = `${data?.name} Inventory`;
      setFileName(fileName);
    } catch (error) {
    }
  };

  const fetchPaginatedInventory = async (page, size, query) => {
    setLoading(true);
    try {
      const sortParam = sortBy
        ? `&sort_by=${sortBy}&sort_order=${sortOrder}`
        : "";
      let url = `/inventory/branch/${branch_id}?page=${page}&pageSize=${size}${sortParam}`;
      if (query) url += `&search=${encodeURIComponent(query)}`;
      const data = await requestWithAuth(url, "GET");
      setInventoryList(data?.results || []);
      setTotalPages(Math.ceil((data?.count || 0) / Number(size)));
      setNextPage(!!data?.next);
      setPrevPage(!!data?.previous);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchAllInventory = async () => {
    try {
      const sortParam = sortBy
        ? `&sort_by=${sortBy}&sort_order=${sortOrder}`
        : "";
      const data = await requestWithAuth(
        `/inventory/branch/${branch_id}?paginated=false${sortParam}`,
        "GET"
      );
      setAllInventory(data || []);
      fullDataFetched.current = true;
      setUsingBackendPagination(false);
    } catch (error) {
    }
  };

  const filterInventoryList = (query) => {
    setSearch(query);
    setCurrentPage(1);
    if (usingBackendPagination) {
      fetchPaginatedInventory(1, pageSize, query);
    }
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  const handleNextPage = () => {
    if (!nextPage) return;
    const nextPageNum = currentPage + 1;
    setCurrentPage(nextPageNum);
    if (usingBackendPagination) {
      fetchPaginatedInventory(nextPageNum, pageSize, search);
    }
  };

  const handlePreviousPage = () => {
    if (!prevPage) return;
    const prevPageNum = currentPage - 1;
    setCurrentPage(prevPageNum);
    if (usingBackendPagination) {
      fetchPaginatedInventory(prevPageNum, pageSize, search);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (usingBackendPagination) {
      fetchPaginatedInventory(pageNumber, pageSize, search);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = e.target.value;
    setPageSize(newSize);
    setCurrentPage(1);
    if (usingBackendPagination) {
      fetchPaginatedInventory(1, newSize, search);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchBranch();
    fullDataFetched.current = false;
    setUsingBackendPagination(true);
    setAllInventory([]);
    setSearch("");
    setCurrentPage(1);

    fetchPaginatedInventory(1, pageSize, "");
    fetchAllInventory();
  }, [branch_id]);

  useEffect(() => {
    if (sortBy === null) return;
    if (usingBackendPagination) {
      fetchPaginatedInventory(1, pageSize, search);
    } else {
      fetchAllInventory();
    }
  }, [sortBy, sortOrder]);

  const syncInventory = async () => {
    Swal.fire({
      icon: "question",
      title: "Sync Branch Inventory?",
      text: "This will add any missing drugs from the company inventory into this branch's inventory.",
      showDenyButton: true,
      confirmButtonText: "Yes, Sync!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSyncing(true);
        try {
          await requestWithAuth(
            `/inventory/branches/${branch_id}/sync-inventory/`,
            "PATCH",
            {}
          );
          Swal.fire({
            icon: "success",
            title: "Synced!",
            text: "Branch inventory successfully synced with company inventory.",
          }).then(() => {
            fetchPaginatedInventory(1, pageSize, "");
            fetchAllInventory();
          });
        } catch (error) {
          toast.error("Failed to sync inventory. Please try again.");
          console.error("Error syncing inventory:", error);
        } finally {
          setSyncing(false);
        }
      }
    });
  };

  useEffect(() => {
    if (usingBackendPagination) return;

    let filtered = allInventory;
    if (search) {
      filtered = allInventory.filter(
        (item) =>
          item?.drug?.name?.toLowerCase().includes(search.toLowerCase()) ||
          item?.drug?.id?.toString().includes(search.toLowerCase())
      );
    }

    let inventoryData = [];
    for (let i = 0; i < filtered.length; i++) {
      inventoryData.push({
        ID: filtered[i]?.drug?.id,
        Drug: filtered[i]?.drug?.name,
        Quantity: filtered[i]?.quantity,
        "Reorder Level": filtered[i]?.reorder_level,
        "Selling Price": filtered[i]?.selling_price,
      });
    }
    setTableData(inventoryData);

    const count = filtered.length;
    setTotalPages(Math.ceil(count / pageSize));

    const startIndex = (currentPage - 1) * pageSize;
    const paginated = filtered.slice(startIndex, startIndex + Number(pageSize));

    setInventoryList(paginated);
    setNextPage(currentPage < Math.ceil(count / pageSize));
    setPrevPage(currentPage > 1);
  }, [usingBackendPagination, allInventory, search, currentPage, pageSize]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Branch</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Inventory</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                {branch?.name} Inventory
              </h1>
            </div>
            <RoleBasedComponent allowedRoles={["superadmin", "admin", "stores"]}>
              <div className="flex gap-3">
                <RoleBasedComponent allowedRoles={["superadmin", "admin"]}>
                  <button
                    onClick={() => setBulkDrugModalOpen(!bulkDrugModalOpen)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
                  >
                    <Upload size={20} />
                    Bulk Drug Upload
                  </button>
                  <button
                    onClick={() => setIsModalOpen(!isModalOpen)}
                    className="bg-white hover:bg-gray-50 text-blue-600 border border-blue-600 px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
                  >
                    <RefreshCw size={20} />
                    Bulk Reconciliation
                  </button>
                </RoleBasedComponent>
                <button
                  onClick={syncInventory}
                  disabled={syncing}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {syncing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <BiSync size={20} />
                  )}
                  {syncing ? "Syncing..." : "Sync Inventory"}
                </button>
              </div>
            </RoleBasedComponent>
          </div>
        </div>

        <BranchBulkReconciliation
          isOpen={isModalOpen}
          onClose={() => window.location.reload()}
          branch_id={branch_id}
        />
        <BranchBulkDrugUploadModal
          isOpen={bulkDrugModalOpen}
          onClose={() => setBulkDrugModalOpen(false)}
          onUploadSuccess={() => {
            fullDataFetched.current = false;
            setUsingBackendPagination(true);
            setAllInventory([]);
            fetchPaginatedInventory(1, pageSize, "");
            fetchAllInventory();
          }}
          branch_id={branch_id}
        />

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData tableData={tableData} fileName={fileName} />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={handlePageSizeChange}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <DebouncedSearchInput
                onSearch={filterInventoryList}
                placeholder="Search inventory..."
                delay={500}
                className="flex-1 max-w-md"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading inventory...</span>
                </div>
              </div>
            ) : inventoryList?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Item Name
                    </th>
                    <SortableTableHeader
                      label="Quantity"
                      fieldKey="quantity"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="Reorder Limit"
                      fieldKey="reorder_level"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cost Price
                    </th>
                    {branch?.is_stores && <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Selling Price
                    </th>}
                    <RoleBasedComponent
                      allowedRoles={["superadmin", "admin", "stores"]}
                    >
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </RoleBasedComponent>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inventoryList?.map((inventory, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm text-gray-900">
                        {inventory?.drug?.id}
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {inventory?.drug?.name}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {inventory?.quantity}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {inventory?.reorder_level}
                      </td>
                      {
                        branch?.is_stores && <td className="py-4 px-6 text-sm text-gray-600">
                          {inventory?.drug?.cost_price}
                        </td>
                      }
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {inventory?.drug?.selling_price}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <RoleBasedComponent
                            allowedRoles={["superadmin", "admin", "stores"]}
                            fallback={null}
                          >
                            <a
                              href={`/inventory/stock/reorder/${inventory?.id}/`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              Edit Reorder
                            </a>
                          </RoleBasedComponent>
                          <RoleBasedComponent
                            allowedRoles={["superadmin", "admin"]}
                            fallback={null}
                          >
                            <a
                              href={`/inventory/stock/reconcile/${inventory?.id}/`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 rounded-lg hover:bg-cyan-100 transition-colors"
                            >
                              Reconcile
                            </a>
                          </RoleBasedComponent>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <Package className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-sm">
                  No inventory items found
                </p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {inventoryList?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BranchInventory;
